import { Editor } from 'slate';
import Tool, { type ToolArgsWithController } from '../../../tools/Tool';
import TableUtils from '../../TablePlugin/TableUtils';
import { type Marks } from '../../../types/aha-editor';
import { COPYABLE_MARKS, COPY_FORMAT_STORAGE_KEY } from './formatCopying';
import { svgToIcon } from '../../../components/Toolbar/toolbarUtils';
import pasteFormatSvg from '../../../assets/icons/pasteFormat.svg';

interface CopiedFormat {
  marks: Partial<Marks>;
}

// Tool to paste the previously copied format to the selected text.
class ToolPasteFormat extends Tool {
  static toolName = 'Paste format';
  static shortcut = 'Cmd+Option+v';
  static ariaLabel = 'Apply copied format to selection';

  static action({ editor, controller }: ToolArgsWithController): void {
    if (!editor?.selection) return;

    // Retrieve the copied format from localStorage
    const storedFormat = localStorage.getItem(COPY_FORMAT_STORAGE_KEY);
    if (!storedFormat) return;

    let copiedFormat: CopiedFormat;
    try {
      copiedFormat = JSON.parse(storedFormat) as CopiedFormat;
    } catch {
      // Invalid JSON in localStorage, ignore
      return;
    }

    if (!copiedFormat.marks) return;

    const tableSelection = controller.state.pluginState.tableSelection;
    const tableWithinSelection = TableUtils.isTableWithinSelection(editor);

    // Helper function to apply all marks
    const applyMarks = () => {
      // First, clear all existing formatting marks from the selection
      const currentMarks = Editor.marks(editor) ?? {};

      for (const mark of COPYABLE_MARKS) {
        if (currentMarks[mark] !== undefined) {
          Editor.removeMark(editor, mark);
        }
      }

      // Apply the copied marks
      Object.entries(copiedFormat.marks).forEach(([markType, markValue]) => {
        if (markValue !== undefined) {
          Editor.addMark(editor, markType, markValue);
        }
      });
    };

    // Apply marks, handling table selections if necessary
    if (tableWithinSelection && tableSelection) {
      TableUtils.applyToTable(editor, tableSelection, applyMarks, {
        hangRange: true,
      });
    } else {
      applyMarks();
    }
  }

  static isEnabled({ editor }: ToolArgsWithController): boolean {
    if (!editor?.selection) return false;

    // Check if there's a copied format in localStorage
    const storedFormat = localStorage.getItem(COPY_FORMAT_STORAGE_KEY);
    return !!storedFormat;
  }

  static renderMenuIcon() {
    return svgToIcon(pasteFormatSvg);
  }
}

export default ToolPasteFormat;
